/**
 * Document File Validation Utility
 * Provides validation functions for document files (PDF, DOC, DOCX)
 * This utility is specific to document uploads and doesn't affect other file types
 */

// Allowed document file types (matching Laravel validation)
export const ALLOWED_DOCUMENT_TYPES = [
  "image/bmp",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/gif",
  "image/jpeg",
  "image/jpg",
  "application/vnd.oasis.opendocument.chart",
  "application/pdf",
  "image/png",
  "application/rtf",
  "text/plain",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/csv",
];

// Allowed document file extensions (matching Laravel validation)
export const ALLOWED_DOCUMENT_EXTENSIONS = [
  ".bmp",
  ".doc",
  ".docx",
  ".gif",
  ".jpeg",
  ".jpg",
  ".odc",
  ".pdf",
  ".png",
  ".rtf",
  ".txt",
  ".xls",
  ".xlsx",
  ".csv",
];

// Maximum file size for documents (10MB - Laravel default)
export const MAX_DOCUMENT_SIZE = 10 * 1024 * 1024; // 10MB in bytes

/**
 * Validate document file type
 * @param {File} file - The file to validate
 * @returns {boolean} - True if file type is valid
 */
export const isValidDocumentType = (file) => {
  if (!file) return false;

  // Check MIME type
  if (ALLOWED_DOCUMENT_TYPES.includes(file.type)) {
    return true;
  }

  // Check file extension as fallback
  const fileExtension = file.name
    .toLowerCase()
    .substring(file.name.lastIndexOf("."));
  return ALLOWED_DOCUMENT_EXTENSIONS.includes(fileExtension);
};

/**
 * Validate document file size
 * @param {File} file - The file to validate
 * @returns {boolean} - True if file size is valid
 */
export const isValidDocumentSize = (file) => {
  if (!file) return false;
  return file.size <= MAX_DOCUMENT_SIZE;
};

/**
 * Get file size in human readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} - Human readable file size
 */
export const getFileSizeString = (bytes) => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

/**
 * Validate document file completely
 * @param {File} file - The file to validate
 * @returns {object} - Validation result with success and error message
 */
export const validateDocumentFile = (file) => {
  if (!file) {
    return {
      isValid: false,
      error: "No file selected",
    };
  }

  // Check file type
  if (!isValidDocumentType(file)) {
    return {
      isValid: false,
      error: `Invalid file type. Please select a valid document file (BMP, DOC, DOCX, GIF, JPEG, JPG, ODC, PDF, PNG, RTF, TXT, XLS, XLSX, CSV).`,
    };
  }

  // Check file size
  if (!isValidDocumentSize(file)) {
    return {
      isValid: false,
      error: `File size too large. Please select a file smaller than ${getFileSizeString(
        MAX_DOCUMENT_SIZE
      )}.`,
    };
  }

  return {
    isValid: true,
    error: null,
  };
};

/**
 * Get accept attribute value for document file inputs
 * @returns {string} - Accept attribute value
 */
export const getDocumentAcceptValue = () => {
  return ".bmp,.doc,.docx,.gif,.jpeg,.jpg,.odc,.pdf,.png,.rtf,.txt,.xls,.xlsx,.csv,image/bmp,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/gif,image/jpeg,image/jpg,application/vnd.oasis.opendocument.chart,application/pdf,image/png,application/rtf,text/plain,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv";
};

/**
 * Get help text for document file inputs
 * @returns {string} - Help text
 */
export const getDocumentHelpText = () => {
  return `Allowed formats: BMP, DOC, DOCX, GIF, JPEG, JPG, ODC, PDF, PNG, RTF, TXT, XLS, XLSX, CSV. Maximum file size: ${getFileSizeString(
    MAX_DOCUMENT_SIZE
  )}.`;
};
